import React from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useSeo } from "../lib/seo";
import { BUSINESS, ADDRESS_ONE_LINE, POSTAL_ADDRESS_LD } from "../lib/business";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Truck, Wrench, Award, ArrowRight } from "lucide-react";

export default function About() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "About Lecis Auto Glass — Exotic & Luxury Auto Glass Specialists",
    description:
      "Learn about Lecis Auto Glass — a Fair Oaks, California auto glass specialist with 15+ years of industry experience supplying side mirrors, windshields, backglass and sunroof glass nationwide.",
    canonicalPath: "/about",
    jsonLd: {
      about: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About Lecis Auto Glass",
        url: `${origin}/about`,
      },
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: BUSINESS.name,
        url: origin || "https://lecisautoglass.com",
        logo: `${origin}/logo.png`,
        email: BUSINESS.email,
        telephone: BUSINESS.phoneHref,
        address: POSTAL_ADDRESS_LD,
        contactPoint: [{
          "@type": "ContactPoint",
          telephone: BUSINESS.phoneHref,
          email: BUSINESS.email,
          contactType: "Customer Service",
          areaServed: "US",
          availableLanguage: "en",
        }],
      },
    },
  });

  return (
    <StorefrontLayout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="about-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
          About Lecis Auto Glass
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6">
          Glass specialists for exotic &amp; luxury vehicles.
        </h1>
        <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-3xl mb-6">
          Lecis Auto Glass is an auto glass specialist based in Fair Oaks, California, serving customers
          nationwide. With <strong className="text-zinc-100">{BUSINESS.experience}</strong>, we focus on
          hard-to-source side mirrors, windshields, backglass and sunroof glass for exotic, luxury and
          EV vehicles — the parts most suppliers won't touch.
        </p>
        <p className="text-zinc-400 leading-relaxed max-w-3xl mb-12">
          Every product is tied to a verified year/make/model/trim fitment record, so you order with
          confidence. We offer both OEM-style and quality aftermarket options, and our team is glad to
          confirm fitment before you buy. Orders ship insured and tracked from our California location
          to your door anywhere in the contiguous United States.
        </p>

        {/* Trust points */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16" data-testid="about-trust-grid">
          {[
            { icon: Award, title: "15+ Years Experience", desc: "Deep expertise in exotic & luxury auto glass." },
            { icon: Truck, title: "Nationwide Shipping", desc: "Insured, tracked delivery across the US." },
            { icon: ShieldCheck, title: "Secure Checkout", desc: "PCI-compliant payments via Stripe." },
            { icon: Wrench, title: "Expert Support", desc: "Fitment help before you order." },
          ].map((t) => (
            <div key={t.title} className="border border-white/10 rounded-md p-6" data-testid={`about-trust-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <t.icon size={20} className="text-red-500 mb-3" />
              <div className="font-display text-lg mb-1">{t.title}</div>
              <div className="text-sm text-zinc-400 leading-relaxed">{t.desc}</div>
            </div>
          ))}
        </div>

        {/* Contact / NAP */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-white/10 pt-12">
          <div>
            <h2 className="text-lg font-display mb-5">Visit or reach us</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-red-500 mt-0.5" />
                <span data-testid="about-address">
                  {BUSINESS.address.line1}<br />
                  {BUSINESS.address.city}, {BUSINESS.address.region} {BUSINESS.address.postalCode}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-red-500" />
                <a href={`tel:${BUSINESS.phoneHref}`} className="hover:text-red-400 transition" data-testid="about-phone">{BUSINESS.phoneDisplay}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-red-500" />
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-red-400 transition" data-testid="about-email">{BUSINESS.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-red-500" />
                <a href={`mailto:${BUSINESS.infoEmail}`} className="hover:text-red-400 transition" data-testid="about-info-email">{BUSINESS.infoEmail}</a>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-lg font-display mb-5">Hours &amp; support</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-red-500 mt-0.5" />
                <span data-testid="about-hours">{BUSINESS.hoursDisplay}</span>
              </li>
              <li className="text-zinc-400" data-testid="about-response-time">{BUSINESS.responseTime}</li>
            </ul>
            <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-2 mt-8 bg-red-600 hover:bg-red-700 text-white px-5 h-11 text-sm rounded-sm transition">
              Contact &amp; Fitment Help <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
